import { z } from 'zod'
import { publicIdSchema, utcTimestampSchema } from './primitives'

/** How a manual field's value counts toward a submission's total. */
export const MANUAL_SCORING_EFFECT_VALUES = ['add', 'deduct', 'record_only'] as const
export const manualScoringEffectSchema = z.enum(MANUAL_SCORING_EFFECT_VALUES)
export type ManualScoringEffect = z.infer<typeof manualScoringEffectSchema>

const finiteScore = z.number().finite().min(-1_000_000).max(1_000_000)

function addRangeIssue(value: { minScore?: number; maxScore?: number }, ctx: z.RefinementCtx) {
  if (
    value.minScore !== undefined &&
    value.maxScore !== undefined &&
    value.maxScore < value.minScore
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['maxScore'],
      message: 'maxScore must not be below minScore.',
    })
  }
}

export const manualFieldDtoSchema = z.strictObject({
  id: publicIdSchema,
  roundId: publicIdSchema,
  name: z.string().trim().min(1).max(80),
  minScore: finiteScore,
  maxScore: finiteScore,
  scoringEffect: manualScoringEffectSchema,
  sortOrder: z.int().min(0),
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
})
export type ManualFieldDto = z.infer<typeof manualFieldDtoSchema>

export const createManualFieldRequestSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(80),
    minScore: finiteScore,
    maxScore: finiteScore,
    scoringEffect: manualScoringEffectSchema,
  })
  .superRefine(addRangeIssue)
export type CreateManualFieldRequest = z.infer<typeof createManualFieldRequestSchema>

export const updateManualFieldRequestSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(80).optional(),
    minScore: finiteScore.optional(),
    maxScore: finiteScore.optional(),
    scoringEffect: manualScoringEffectSchema.optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be provided.',
  })
  .superRefine(addRangeIssue)
export type UpdateManualFieldRequest = z.infer<typeof updateManualFieldRequestSchema>

/** One cell: a staff-entered value for a submission and field. */
export const manualEntryDtoSchema = z.strictObject({
  id: publicIdSchema,
  roundId: publicIdSchema,
  fieldId: publicIdSchema,
  submissionId: publicIdSchema,
  value: z.number(),
  enteredByUserId: publicIdSchema,
  enteredByDisplayName: z.string(),
  enteredAt: utcTimestampSchema,
  updatedByUserId: publicIdSchema,
  updatedByDisplayName: z.string(),
  updatedAt: utcTimestampSchema,
})
export type ManualEntryDto = z.infer<typeof manualEntryDtoSchema>

export const saveManualEntriesRequestSchema = z.strictObject({
  submissionId: publicIdSchema,
  entries: z
    .array(z.strictObject({ fieldId: publicIdSchema, value: finiteScore }))
    .min(1)
    .max(100),
})
export type SaveManualEntriesRequest = z.infer<typeof saveManualEntriesRequestSchema>

/** One row of the staff entry grid: a submission of the round and its current entries. */
export const manualEntryRowDtoSchema = z.strictObject({
  submissionId: publicIdSchema,
  categoryId: publicIdSchema,
  teamName: z.string(),
  entries: z.array(manualEntryDtoSchema),
})
export type ManualEntryRowDto = z.infer<typeof manualEntryRowDtoSchema>

export const manualEntryGridDtoSchema = z.strictObject({
  roundId: publicIdSchema,
  /** Whether staff may write now (mode allows staff, round open or judging, results not locked). */
  writable: z.boolean(),
  /** Why `writable` is false; null when it is true. */
  readonlyReason: z.enum(['mode_disallows_staff', 'round_not_open', 'results_locked']).nullable(),
  fields: z.array(manualFieldDtoSchema),
  rows: z.array(manualEntryRowDtoSchema),
})
export type ManualEntryGridDto = z.infer<typeof manualEntryGridDtoSchema>

export const manualEntryRevisionDtoSchema = z.strictObject({
  id: publicIdSchema,
  entryId: publicIdSchema,
  fieldId: publicIdSchema,
  fieldName: z.string(),
  submissionId: publicIdSchema,
  oldValue: z.number().nullable(),
  newValue: z.number(),
  changedByUserId: publicIdSchema,
  changedByDisplayName: z.string(),
  changedAt: utcTimestampSchema,
})
export type ManualEntryRevisionDto = z.infer<typeof manualEntryRevisionDtoSchema>

/**
 * Score detail for one submission with every line labelled by source: judge
 * scores and staff-entered values side by side, plus the revision history of
 * the staff values. Staff-entered lines carry the field name and its effect.
 */
export const scoreBreakdownDtoSchema = z.strictObject({
  roundId: publicIdSchema,
  scoringInputMode: z.enum(['judge_only', 'staff_only', 'judge_and_staff']),
  judgeScores: z.array(
    z.strictObject({
      source: z.literal('judge'),
      scoreId: publicIdSchema,
      judgeDisplayName: z.string(),
      status: z.enum(['draft', 'submitted', 'locked', 'reopened']),
      totalScore: z.number().nullable(),
    }),
  ),
  manualScores: z.array(
    z.strictObject({
      source: z.literal('staff'),
      entryId: publicIdSchema,
      fieldId: publicIdSchema,
      fieldName: z.string(),
      scoringEffect: manualScoringEffectSchema,
      value: z.number(),
      enteredByDisplayName: z.string(),
      updatedByDisplayName: z.string(),
      updatedAt: utcTimestampSchema,
    }),
  ),
  revisions: z.array(manualEntryRevisionDtoSchema),
})
export type ScoreBreakdownDto = z.infer<typeof scoreBreakdownDtoSchema>
